/**
 * Domyślny proces (obieg akceptacji) per typ rekordu — wspólne elementy SPA dla słowników typów
 * dokumentów i typów zadań: lista opublikowanych procesów celujących w daną kartotekę, opcje filtra
 * kolumny i mapowanie kodów błędu walidacji domyślnego procesu na komunikaty (wspólny walidator
 * serwera dla obu kartotek: `apps/api/lib/record-types/validate-default-process.ts`).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { ToastContextValue } from '@/contexts/ToastContext';
import { listProcesses } from '@/lib/processes/api';
import { NONE_FILTER_VALUE } from '@/lib/record-types/form-layout';

/** Opcja pickera domyślnego procesu. */
export interface DefaultProcessOption {
  id: string;
  name: string;
}

/**
 * Opublikowane procesy celujące w kartotekę `targetModuleCode`. Filtr celu jest WYMOGIEM
 * poprawności: serwer odrzuca proces o innym celu (`DEFAULT_PROCESS_MODULE_MISMATCH`), więc nie
 * pokazujemy opcji, której zapis by nie przyjął. `enabled` — widok słownika bywa tylko-do-odczytu,
 * ale nazwę procesu w kolumnie też trzeba rozwiązać.
 */
export function useDefaultProcessOptions(
  targetModuleCode: string,
  enabled: boolean,
): { processes: DefaultProcessOption[]; loading: boolean } {
  const processesQ = useQuery({
    queryKey: ['processes', 'default-picker', targetModuleCode],
    queryFn: () => listProcesses({ isPublished: true, targetModuleCode }),
    enabled,
  });
  const rows = processesQ.data?.data;
  const processes = useMemo(() => (rows ?? []).map((p) => ({ id: p.id, name: p.name })), [rows]);
  // `loading` = lista jeszcze się nie rozstrzygnęła (trwa pobieranie albo zapytanie wyłączone);
  // po błędzie lista jest rozstrzygnięta (pusta) — wtedy pole może już pokazać wskazanie spoza listy.
  return { processes, loading: processesQ.isPending };
}

/** Opcje filtra kolumny „Proces domyślny": „— brak —" (znacznik `NONE_FILTER_VALUE`) + procesy. */
export function defaultProcessFilterOptions(
  processes: DefaultProcessOption[],
  t: TFunction,
): { value: string; label: string }[] {
  return [
    { value: NONE_FILTER_VALUE, label: t('tenant.recordTypes.defaultProcess.none') },
    ...processes.map((p) => ({ value: p.id, label: p.name })),
  ];
}

/**
 * Mapuje kody walidacji domyślnego procesu na toast ostrzegawczy. `true` = kod obsłużony (wołający
 * przerywa przed generycznym fallbackiem), `false` = inny kod. Lustro `handleFormLayoutErrorCode`.
 */
export function handleDefaultProcessErrorCode(
  code: string | null | undefined,
  toast: ToastContextValue,
  t: TFunction,
): boolean {
  const message = defaultProcessErrorMessage(code, t);
  if (message === null) return false;
  toast.show({ variant: 'warning', title: message });
  return true;
}

/**
 * Komunikat dla kodu błędu walidacji domyślnego procesu (`DEFAULT_PROCESS_NOT_FOUND` /
 * `_NOT_PUBLISHED` / `_MODULE_MISMATCH` / `_TYPE_NOT_ALLOWED` / `_NO_CLASSIFIERS` — wspólny
 * walidator serwera `apps/api/lib/record-types/validate-default-process.ts`) albo `null`, gdy to
 * inny kod (wołający obsługuje go sam).
 */
export function defaultProcessErrorMessage(
  code: string | null | undefined,
  t: TFunction,
): string | null {
  switch (code) {
    case 'DEFAULT_PROCESS_NOT_FOUND':
      return t('tenant.recordTypes.defaultProcess.notFound');
    case 'DEFAULT_PROCESS_NOT_PUBLISHED':
      return t('tenant.recordTypes.defaultProcess.notPublished');
    case 'DEFAULT_PROCESS_MODULE_MISMATCH':
      return t('tenant.recordTypes.defaultProcess.moduleMismatch');
    case 'DEFAULT_PROCESS_TYPE_NOT_ALLOWED':
      return t('tenant.recordTypes.defaultProcess.typeNotAllowed');
    case 'DEFAULT_PROCESS_NO_CLASSIFIERS':
      return t('tenant.recordTypes.defaultProcess.noClassifiers');
    default:
      return null;
  }
}
